/* Stallholder logo strip (sections/markets.liquid). Source:
   components/patterns/LogoCarousel.jsx in the design system: a row of equal
   slots (--logo-per-view, set in section-markets.css: 3 below 750px, 5 from
   750px), stepping one slot every 3.2s, greyscale until hovered. Deliberate
   divergence: it moves only when there are more venues than slots, because the
   design system forbids motion that is not doing work; with fewer or equal it
   stays a static, centred row. Pauses on hover and focus; never moves under
   prefers-reduced-motion, and re-checks when that preference changes. Without
   JS (or with reduced motion) the slots wrap as a static grid. */
if (!customElements.get('logo-carousel')) {
  class LogoCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;
      this.step = 0;
      this.paused = false;
      this.perView = 0;
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.onPreference = () => this.layout();
      this.motion.addEventListener('change', this.onPreference);
      this.addEventListener('mouseenter', () => (this.paused = true));
      this.addEventListener('mouseleave', () => (this.paused = false));
      this.addEventListener('focusin', () => (this.paused = true));
      this.addEventListener('focusout', () => (this.paused = false));
      this.resizer = new ResizeObserver(() => this.resize());
      this.resizer.observe(this);
      this.layout();
    }

    disconnectedCallback() {
      if (!this.track) return;
      this.stop();
      if (this.resizer) this.resizer.disconnect();
      if (this.motion) this.motion.removeEventListener('change', this.onPreference);
    }

    readPerView() {
      return parseInt(getComputedStyle(this).getPropertyValue('--logo-per-view'), 10) || 5;
    }

    stop() {
      clearInterval(this.timer);
      clearTimeout(this.snap);
      this.timer = null;
      this.track.querySelectorAll('[data-clone]').forEach((node) => node.remove());
      this.track.style.transform = '';
      this.track.style.transition = '';
      this.classList.remove('is-moving');
      this.step = 0;
    }

    // Width changes only re-seat the running strip (slot widths are percentages,
    // so the offset must follow). It restarts only if per-view changed.
    resize() {
      const perView = this.readPerView();
      if (perView !== this.perView) return this.layout();
      if (this.classList.contains('is-moving')) {
        this.track.style.transition = 'none';
        this.track.style.transform = this.step ? `translateX(-${this.track.children[this.step].offsetLeft}px)` : '';
      }
    }

    layout() {
      this.stop();
      this.perView = this.readPerView();
      if (this.motion.matches) return;
      this.originals = Array.from(this.track.children);
      if (this.originals.length <= this.perView) return;

      this.originals.forEach((item) => {
        const clone = item.cloneNode(true);
        clone.setAttribute('data-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
        this.track.appendChild(clone);
      });
      this.classList.add('is-moving');
      this.timer = setInterval(() => this.advance(), 3200);
    }

    advance() {
      if (this.paused || document.hidden) return;
      this.step += 1;
      const items = this.track.children;
      this.track.style.transition = '';
      this.track.style.transform = `translateX(-${items[this.step].offsetLeft}px)`;
      if (this.step === this.originals.length) {
        // The first clone now sits where the first logo started: snap back unseen.
        // A timeout rather than transitionend, which never fires if the
        // transition is skipped (background tab, reduced motion mid-step). The
        // delay is the CSS transition (--duration-carousel) plus 100ms, read from
        // the computed style so the two cannot drift apart.
        const duration = parseFloat(getComputedStyle(this.track).transitionDuration) || 0;
        this.snap = setTimeout(() => {
          this.track.style.transition = 'none';
          this.track.style.transform = '';
          this.step = 0;
        }, duration * 1000 + 100);
      }
    }
  }
  customElements.define('logo-carousel', LogoCarousel);
}
